import { css } from 'lit';
/* eslint-disable max-lines */
/* eslint-disable camelcase  */
import { GeoJsonGeometryTypes } from 'geojson';
import { ComboBoxOption } from 'cra-web-components/components/combo-box/combo-box';
import {
	isEventAbandonedVehicleWithValidTimestamps,
	isEventInRegion,
	isValidNumber,
} from '@/utils/utils';
import {
  DMSStatusType,
  DraftEvent,
  EventStatus,
  LaneImpactValue,
  LocationTypes,
  RoadEventDto,
} from '../../typings/api';
import { EventAttribute } from '../components/rem/rem-attributes';
import {
  MILLISECONDS_PER_HOUR,
  MILLISECONDS_PER_SECOND,
  REM_EVENT_COLUMNS,
  Region,
  RelativeDateTime,
  RelativeDuration,
} from '../constants';

import { EventType, EventTypes } from './ConfigREMEventTypes';
import { Districts } from './ConfigCARSx';

// list of all REM event form section names
// these form sections will be displayed in the same order as listed here
export const REM_FORM_SECTIONS = {
  DESCRIPTION: 'description',
  SOURCE: 'source',
  LOCATION: 'location',
  VEHICLES: 'vehicles',
  RESTRICTIONS: 'restrictions',
  DETOURS: 'detours',
  NOTES: 'notes',
  VISUAL_VERIFICATION: 'visual verification',
  ATTACHMENTS: 'attachments',
  SCHEDULING: 'scheduling',
  DMS: 'dms',
  RESPONDING_UNITS: 'responding units',
  ATTRIBUTES: 'attributes',
  LINKING: 'linking',
  PROJECT_CONTACTS: 'project contacts',
  NOTIFICATIONS: 'notifications',
  CAMERAS: 'cameras',
} as const;

export type FormSectionsRecord<T> = {
  [K in keyof typeof REM_FORM_SECTIONS]: T;
};

interface FormSectionConfigOptions {
  active: boolean;
  toggleable: boolean;
}

export const FormSectionConfig: FormSectionsRecord<FormSectionConfigOptions> = {
  DESCRIPTION: {
    active: true,
    toggleable: false,
  },
  SOURCE: {
    active: false,
    toggleable: false,
  },
  LOCATION: {
    active: true,
    toggleable: false,
  },
  VEHICLES: {
    active: true,
    toggleable: true,
  },
  RESTRICTIONS: {
    active: true,
    toggleable: false,
  },
  DETOURS: {
    active: true,
    toggleable: false,
  },
  NOTES: {
    active: true,
    toggleable: false,
  },
  VISUAL_VERIFICATION: {
    active: false,
    toggleable: false,
  },
  	ATTACHMENTS: {
		active: false,
		toggleable: false,
	},
  SCHEDULING: {
    active: true,
    toggleable: false,
  },
  DMS: {
    active: false,
    toggleable: false,
  },
  RESPONDING_UNITS: {
    active: false,
    toggleable: false,
  },
  ATTRIBUTES: {
    active: false,
    toggleable: false,
  },
  LINKING: {
    active: false,
    toggleable: false,
  },
  NOTIFICATIONS: {
    active: true,
    toggleable: false,
  },
  PROJECT_CONTACTS: {
    active: true,
    toggleable: false,
  },
  CAMERAS: {
    active: false,
    toggleable: false,
  },
};

export const ConfigREMMap = {
  //  Leaflet map will center on this point, at the provided zoom level
  //  https://leafletjs.com/reference-1.4.0.html#map-setview
  defaultCenter: {
    lat: 44.972675,
    lng: -93.274995,
  },
  defaultZoom: 10,
  MinZoom: 7,
  MaxZoom: 18,
  pollingDelayMs: 10 * MILLISECONDS_PER_SECOND,
  fitBoundsOptions: {
    padding: 30,
    maxZoom: 13,
    minZoom: 9,
  },
  showQuickFilters: true,
  	eventPopup: {
		showInternalContactDetails: true,
		showProjectContacts: true,
	},
};

export enum REMExternalFilterOptions {
  ALL = 'All',
  AE = 'Incidents',
  CM = 'CONST/MAINT',
  FUTURE = 'Future',
  RESTRICT = 'RESTRICTIONS',
  HW = 'FLOODING',
  ROUNDABOUTS = 'Roundabouts',
  IMPORTED = 'Imported',
  CLOSED = 'Closed',
}

export const REMLiteTableFilters: REMExternalFilterOptions[] = [
  REMExternalFilterOptions.AE,
];

interface ExternalFilterOptionsConfig {
  excludeCompletedEvents: boolean;
  includedEventTypes: EventType[];
  excludedEventTypes: EventType[];
  additionalEventChecks?: Array<(event: RoadEventDto) => boolean>;
  onlyShowImportedEvents?: boolean;
  showFutureEvents?: boolean;
  hideCurrentEvents?: boolean;
}

export const ConfigREMTable = {
  pagination: [15, 25, 50, 100],
  defaultPagination: 3,
  pollingDelayMs: 10 * MILLISECONDS_PER_SECOND,
  defaultColumns: [
    'type',
    'id',
    'route',
    'mileMarker',
    'direction',
    'district',
    'lastUser',
    'lastUpdated',
  ] as typeof REM_EVENT_COLUMNS[number][],
  externalFilters: {
    [REMExternalFilterOptions.ALL]: {
      excludeCompletedEvents: true,
      includedEventTypes: [],
      excludedEventTypes: [],
      showFutureEvents: true,
    },
    [REMExternalFilterOptions.AE]: {
      excludeCompletedEvents: true,
      includedEventTypes: [],
      excludedEventTypes: [
        EventTypes.FLOODING,

        // Category 4
        EventTypes.ROAD_CONSTRUCTION,
        EventTypes.CONSTRUCTION_WORK,
        EventTypes.PAVING_OPERATIONS,
        EventTypes.WORK_IN_THE_MEDIAN,
        EventTypes.ROAD_RECONSTRUCTION,
        EventTypes.NARROW_LANES,
        EventTypes.CONSTRUCTION_TRAFFIC_MERGING,
        EventTypes.ROAD_MAINTENANCE_OPERATIONS,
        EventTypes.BRIDGE_MAINTENANCE_OPERATIONS,
        EventTypes.BRIDGE_CONSTRUCTION,
        EventTypes.BRIDGE_DEMOLITION_WORK,
        EventTypes.WATER_MAIN_WORK,
        EventTypes.GAS_MAIN_WORK,
        EventTypes.WORK_ON_UNDERGROUND_CABLES,
        EventTypes.WORK_ON_UNDERGROUND_SERVICES,
        EventTypes.NEW_ROAD_CONSTRUCTION_LAYOUT,
        EventTypes.TEMPORARY_TRAFFIC_LIGHTS,
        EventTypes.EMERGENCY_MAINTENANCE,
        EventTypes.BRIDGE_INSPECTION_WORK,
        EventTypes.NIGHT_TIME_CONSTRUCTION_WORK,
        EventTypes.WORK_ON_THE_SHOULDER,
        EventTypes.EMERGENCY_REPAIRS,
        EventTypes.UTILITY_WORK,
        EventTypes.BRUSH_CONTROL,
        EventTypes.HEAD_TO_HEAD_TRAFFIC_ON_THE_ROADWAY,
        EventTypes.PORTABLE_TRAFFIC_SIGNALS_IN_USE,
        EventTypes.SINGLE_LANE_TRAFFIC_ALTERNATING_DIRECTIONS,
        EventTypes.WORKERS_ARE_PRESENT,
        EventTypes.WORK_ZONE_IS_ACTIVE,
        EventTypes.MAJOR_CONSTRUCTION_PROJECT,

        // Category 10
        EventTypes.RESTRICTIONS,
        EventTypes.RAMP_RESTRICTIONS,
        EventTypes.TRUCK_RESTRICTION,
        EventTypes.SPEED_RESTRICTION,
        EventTypes.NOISE_RESTRICTION,
        EventTypes.TRAFFIC_REGULATIONS_HAVE_BEEN_CHANGED,
        EventTypes.LOCAL_ACCESS_ONLY,
        EventTypes.NO_TRAILERS,
        EventTypes.NO_HIGH_PROFILE_VEHICLES,
        EventTypes.HAZARDOUS_MATERIALS_TRUCK_RESTRICTION,
        EventTypes.NO_THROUGH_TRAFFIC,
        EventTypes.NO_MOTOR_VEHICLES,
        EventTypes.WIDTH_LIMIT,
        EventTypes.HEIGHT_LIMIT,
        EventTypes.LENGTH_LIMIT,
        EventTypes.AXLE_LOAD_LIMIT,
        EventTypes.GROSS_WEIGHT_LIMIT,
        EventTypes.AXLE_COUNT_LIMIT,
        EventTypes.CARPOOL_LANE_AVAILABLE,
        EventTypes.CARPOOL_RESTRICTIONS_CHANGED,
        EventTypes.HOV_2_NO_SINGLE_OCCUPANT_VEHICLES,
        EventTypes.HOV_3_NO_VEHICLES_WITH_LESS_THAN_THREE_OCCUPANTS,
        EventTypes.BUS_LANE_AVAILABLE_FOR_ALL_VEHICLES,
        EventTypes.TRUCK_LANE_AVAILABLE_FOR_ALL_VEHICLES,
        EventTypes.PERMITS_CALL_IN_BASIS,
        EventTypes.PERMITS_TEMPORARILY_CLOSED,
        EventTypes.PERMITS_CLOSED,
        EventTypes.RESTRICTIONS_FOR_HIGH_PROFILE_VEHICLES_LIFTED,
        EventTypes.CARPOOL_RESTRICTIONS_LIFTED,
        EventTypes.LANE_RESTRICTIONS_LIFTED,
        EventTypes.RAMP_RESTRICTIONS_LIFTED,
        EventTypes.MOTOR_VEHICLE_RESTRICTIONS_LIFTED,
        EventTypes.RESTRICTIONS_LIFTED,
        EventTypes.TOWING_SERVICES_PROHIBITED,
        EventTypes.SEASONAL_WEIGHT_LIMIT_50_PERCENT_OF_LEGAL_LOAD,
        EventTypes.SEASONAL_WEIGHT_LIMIT_75_PERCENT_OF_LEGAL_LOAD,
        EventTypes.SEASONAL_WEIGHT_LIMIT_85_PERCENT_OF_LEGAL_LOAD,
        EventTypes.SEASONAL_WEIGHT_LIMIT_100_PERCENT_OF_LEGAL_LOAD,
        EventTypes.EMERGENCY_WEIGHT_LIMIT_50_PERCENT_OF_LEGAL_LOAD,
        EventTypes.EMERGENCY_WEIGHT_LIMIT_75_PERCENT_OF_LEGAL_LOAD,
        EventTypes.EMERGENCY_WEIGHT_LIMIT_85_PERCENT_OF_LEGAL_LOAD,
        EventTypes.EMERGENCY_WEIGHT_LIMIT_100_PERCENT_OF_LEGAL_LOAD,
        EventTypes.NO_OVERLOADS,
        EventTypes.PERCENT_100_OF_LEGAL_LOAD_ALLOWED_ON_THE_DRIVE_AXLES_OF_LONG_COMBINATION_VEHICLES,
        EventTypes.LONG_COMBINATION_VEHICLES_PROHIBITED,
        EventTypes.PERMITTED_OVERSIZE_LOADS_PROHIBITED,
        EventTypes.NO_COMMERCIAL_TRAILER_TRAFFIC,
        EventTypes.POSTED_BRIDGE,
        EventTypes.TRUCKER_INFO,
        EventTypes.ROUNDABOUT,
      ],
      additionalEventChecks: [
        (event: RoadEventDto) => !isEventAbandonedVehicleWithValidTimestamps(event),
      ],
    },
    [REMExternalFilterOptions.IMPORTED]: {
      excludeCompletedEvents: true,
      includedEventTypes: [],
      excludedEventTypes: [],
      onlyShowImportedEvents: true,
    },

    [REMExternalFilterOptions.CM]: {
      excludeCompletedEvents: true,
      includedEventTypes: [
        EventTypes.ROAD_CONSTRUCTION,
        EventTypes.CONSTRUCTION_WORK,
        EventTypes.PAVING_OPERATIONS,
        EventTypes.WORK_IN_THE_MEDIAN,
        EventTypes.ROAD_RECONSTRUCTION,
        EventTypes.NARROW_LANES,
        EventTypes.CONSTRUCTION_TRAFFIC_MERGING,
        EventTypes.ROAD_MAINTENANCE_OPERATIONS,
        EventTypes.BRIDGE_MAINTENANCE_OPERATIONS,
        EventTypes.BRIDGE_CONSTRUCTION,
        EventTypes.BRIDGE_DEMOLITION_WORK,
        EventTypes.WATER_MAIN_WORK,
        EventTypes.GAS_MAIN_WORK,
        EventTypes.WORK_ON_UNDERGROUND_CABLES,
        EventTypes.WORK_ON_UNDERGROUND_SERVICES,
        EventTypes.NEW_ROAD_CONSTRUCTION_LAYOUT,
        EventTypes.TEMPORARY_TRAFFIC_LIGHTS,
        EventTypes.EMERGENCY_MAINTENANCE,
        EventTypes.BRIDGE_INSPECTION_WORK,
        EventTypes.NIGHT_TIME_CONSTRUCTION_WORK,
        EventTypes.WORK_ON_THE_SHOULDER,
        EventTypes.EMERGENCY_REPAIRS,
        EventTypes.UTILITY_WORK,
        EventTypes.BRUSH_CONTROL,
        EventTypes.HEAD_TO_HEAD_TRAFFIC_ON_THE_ROADWAY,
        EventTypes.PORTABLE_TRAFFIC_SIGNALS_IN_USE,
        EventTypes.SINGLE_LANE_TRAFFIC_ALTERNATING_DIRECTIONS,
        EventTypes.WORKERS_ARE_PRESENT,
        EventTypes.WORK_ZONE_IS_ACTIVE,
        EventTypes.MAJOR_CONSTRUCTION_PROJECT,
      ],
      excludedEventTypes: [],
    },
    [REMExternalFilterOptions.RESTRICT]: {
      excludeCompletedEvents: true,
      includedEventTypes: [
        EventTypes.RESTRICTIONS,
        EventTypes.RAMP_RESTRICTIONS,
        EventTypes.TRUCK_RESTRICTION,
        EventTypes.SPEED_RESTRICTION,
        EventTypes.NOISE_RESTRICTION,
        EventTypes.TRAFFIC_REGULATIONS_HAVE_BEEN_CHANGED,
        EventTypes.LOCAL_ACCESS_ONLY,
        EventTypes.NO_TRAILERS,
        EventTypes.NO_HIGH_PROFILE_VEHICLES,
        EventTypes.HAZARDOUS_MATERIALS_TRUCK_RESTRICTION,
        EventTypes.NO_THROUGH_TRAFFIC,
        EventTypes.NO_MOTOR_VEHICLES,
        EventTypes.WIDTH_LIMIT,
        EventTypes.HEIGHT_LIMIT,
        EventTypes.LENGTH_LIMIT,
        EventTypes.AXLE_LOAD_LIMIT,
        EventTypes.GROSS_WEIGHT_LIMIT,
        EventTypes.AXLE_COUNT_LIMIT,
        EventTypes.CARPOOL_LANE_AVAILABLE,
        EventTypes.CARPOOL_RESTRICTIONS_CHANGED,
        EventTypes.HOV_2_NO_SINGLE_OCCUPANT_VEHICLES,
        EventTypes.HOV_3_NO_VEHICLES_WITH_LESS_THAN_THREE_OCCUPANTS,
        EventTypes.BUS_LANE_AVAILABLE_FOR_ALL_VEHICLES,
        EventTypes.TRUCK_LANE_AVAILABLE_FOR_ALL_VEHICLES,
        EventTypes.PERMITS_CALL_IN_BASIS,
        EventTypes.PERMITS_TEMPORARILY_CLOSED,
        EventTypes.PERMITS_CLOSED,
        EventTypes.RESTRICTIONS_FOR_HIGH_PROFILE_VEHICLES_LIFTED,
        EventTypes.CARPOOL_RESTRICTIONS_LIFTED,
        EventTypes.LANE_RESTRICTIONS_LIFTED,
        EventTypes.RAMP_RESTRICTIONS_LIFTED,
        EventTypes.MOTOR_VEHICLE_RESTRICTIONS_LIFTED,
        EventTypes.RESTRICTIONS_LIFTED,
        EventTypes.TOWING_SERVICES_PROHIBITED,
        EventTypes.SEASONAL_WEIGHT_LIMIT_50_PERCENT_OF_LEGAL_LOAD,
        EventTypes.SEASONAL_WEIGHT_LIMIT_75_PERCENT_OF_LEGAL_LOAD,
        EventTypes.SEASONAL_WEIGHT_LIMIT_85_PERCENT_OF_LEGAL_LOAD,
        EventTypes.SEASONAL_WEIGHT_LIMIT_100_PERCENT_OF_LEGAL_LOAD,
        EventTypes.EMERGENCY_WEIGHT_LIMIT_50_PERCENT_OF_LEGAL_LOAD,
        EventTypes.EMERGENCY_WEIGHT_LIMIT_75_PERCENT_OF_LEGAL_LOAD,
        EventTypes.EMERGENCY_WEIGHT_LIMIT_85_PERCENT_OF_LEGAL_LOAD,
        EventTypes.EMERGENCY_WEIGHT_LIMIT_100_PERCENT_OF_LEGAL_LOAD,
        EventTypes.NO_OVERLOADS,
        EventTypes.PERCENT_100_OF_LEGAL_LOAD_ALLOWED_ON_THE_DRIVE_AXLES_OF_LONG_COMBINATION_VEHICLES,
        EventTypes.LONG_COMBINATION_VEHICLES_PROHIBITED,
        EventTypes.PERMITTED_OVERSIZE_LOADS_PROHIBITED,
        EventTypes.NO_COMMERCIAL_TRAILER_TRAFFIC,
        EventTypes.POSTED_BRIDGE,
        EventTypes.TRUCKER_INFO,
        EventTypes.ROUNDABOUT,
      ],
      excludedEventTypes: [],
    },
    [REMExternalFilterOptions.HW]: {
      excludeCompletedEvents: true,
      includedEventTypes: [EventTypes.FLOODING],
      excludedEventTypes: [],
    },
    [REMExternalFilterOptions.FUTURE]: {
      excludeCompletedEvents: true,
      includedEventTypes: [],
      excludedEventTypes: [],
      hideCurrentEvents: true,
      showFutureEvents: true,
    },
    [REMExternalFilterOptions.ROUNDABOUTS]: {
      excludeCompletedEvents: false,
      includedEventTypes: [EventTypes.ROUNDABOUT],
      excludedEventTypes: [],
    },
    [REMExternalFilterOptions.CLOSED]: {
      excludeCompletedEvents: false,
      includedEventTypes: [],
      excludedEventTypes: [],
      additionalEventChecks: [(event: RoadEventDto) => event.eventStatus === EventStatus.COMPLETED],
    },
  } as Partial<Record<REMExternalFilterOptions, ExternalFilterOptionsConfig>>,
};
// Configures the URLS for each endpoint to be used by the REM module
export const ConfigREMApi = {
  fields: (): string => `events/fields`,
  email: (): string => `events/email`,
  deleteEmail: (): string => `events/email/delete`,
  events: (): string => `events`,

  eventImports: (): string => `imports/events`,
  takeoverEvent: (eventId: number): string => `imports/events/${eventId}/takeover`,
  forgetEvent: (): string => `imports/events/forget`,

  workZoneRequests: `events/wzrt`,
	getWorkZoneRequests: `events/publish`,

	event: (eventId: number): string => `events/${eventId}`,
	eventTimeline: (eventId: number): string => `events/${eventId}/timeline`,
	createOrUpdate: (): string => `events`,
	routesByCountyDistrict: (): string => `locations/route`,
	countyDistrict: (route_designator: string): string =>
		`locations/route/${encodeURIComponent(route_designator)}`,
	nearbyCameras: (route_designator: string): string =>
		`locations/route/${route_designator}/cameras`,
	dmsPreviewsByREMEvent: (): string => `sign/previews`,
	dmsSignsForEvent: (): string => `sign/save/previews`,
	getSignById: (signId: number): string => `sign/id/${signId}`,
	getSignQueueById: (signId: number, signType: DMSStatusType = DMSStatusType.ACTIVE): string =>
		`sign/queue/${signType}/${signId}`,
	setDMSforRemEvent: (): string => `/api/sign/save/messages`,
	getDMSForRemEvent: (eventId: number): string => `sign/messages/${eventId}`,
	removeDMSByREMEvent: (signId: number): string => `sign/trash/${signId}`,
	currentEditors: (eventId: number): string => `/api/events/${eventId}/currentEditors`,
	allCurrentEditors: `/api/events/currentEditors`,
	prioritizeMessagesForEvent: (eventId: number): string => `/api/events/${eventId}/refresh`,
	routeExtent: (): string => `locations/geometry`,
	namedPoints: (route: string) => `locations/route/${encodeURIComponent(route)}/named-points`,
	areas: (areaType: string) => `locations/areas/${areaType}`,
	codedRoadway: (route: string) => `locations/route/${encodeURIComponent(route)}/coded-roadway`,
	eventIcon: (id: number) => `icons/events/${id}`,
	eventIconBase: () => `icons/events`,
	routesWithRamps: () => `locations/ramps/routes`,
	rampsForRoute: (route: string) => `locations/ramps/${encodeURIComponent(route)}`,
	snapToRoutePoint: () => `locations/route/snapToRoutePoint`,
	ramp: (route: string, rampId: number) => `locations/ramps/${encodeURIComponent(route)}/${rampId}`,
	duplicateEvent: (id: number) => `events/${id}/duplicate`,
	attachments: (eventId: number) => `events/${eventId}/attachments`,

	snapToRamp: `locations/route/snapToRamp`,
	requestTimeoutMs: 30 * MILLISECONDS_PER_SECOND,
	signPollingRate: 60 * MILLISECONDS_PER_SECOND,
  heartbeatFrequencyMs: 5 * MILLISECONDS_PER_SECOND,
	dateFormat: 'YYYY-MM-DDTHH:mm:ss.SSSSZ',
};

export const ConfigREMEventForm = {
	location: {
		/*
			<input pattern="" /> regex is slightly different than javascript
			equivalent pattern in javascript: /^(?:\d{1,3}(?:\.\d{1})?)$/gm
		*/
		mileMarkerPattern: '[0-9]{1,3}(?:\\.[0-9]{1})?',
		mileMarkerInvalidPattern: 'Value must be formatted "mmm.t"',
		mileMarkerValueFitted: 'Mile marker entry snapped to nearest valid value.',
		mileMarkerEndValueInvalid: 'Invalid end value removed.',
		displayDistrict: true,
		displaySubdistrict: false,
		displayUnit: false,
		displayLaneDiagram: true,
	},
	source: {
		// FIXME: consider using intl-tel-input npm package in the future
		phoneNumberRegex: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
		phoneNumberInvalid: 'Warning: phone number may not be formatted correctly',
		ordering: [
      'MSPCAD',
      'Work Zone Request',
      'MNDOT',
    ],
    defaultSource: 'MNDOT',
	},
	affectedLanes: {
		defaultLaneBlockageType: LaneImpactValue.NA,
		showRampLaneDiagram: false,
		showLaneImpactTypes: false,
		showQuickDirectionCheckboxes: true,
	},
	vehicles: {
		defaultLicensePlateState: 'N/A',
		licensePlateNumberDelimitter: '###',
	},
	dms: {
		prioritizeMessagesToolTip:
			'Clicking the <span style="color: rgb(var(--brand-status-caution));">Refresh Queue</span> button will resort the message queues for all signs associated with this event. The message associated with this event will be displayed on the board unless there are any messages with a higher priority in the queue.',
		messagesCanBeCustomized: true,
	},
	attachments: {
		fileTooltips: ['Attachments must be image, text or video files (e.g. png, jpg, txt, pdf, mp4)'],
	},

	//	scheduling formats
	wholeDatestampFormatOptions: {
		//	for displaying the current date and time in full
		hour: '2-digit',
		minute: '2-digit',
		month: 'short',
		day: '2-digit',
		year: 'numeric',
		timeZoneName: 'short',
	} as Intl.DateTimeFormatOptions,
	schedulingDateFormatOptions: {
		//	for populating the date selector inputs
	},
	schedulingTimeFormatOptions: {
		//	for populating the time selector inputs
		hour12: false,
		hour: '2-digit',
		minute: '2-digit',
	} as Intl.DateTimeFormatOptions,
	timelineDateFormatOptions: {
		//	for the daily timeline entry headlines
		month: 'short',
		day: '2-digit',
		year: 'numeric',
	} as Intl.DateTimeFormatOptions,
	timelineTimeFormatOptions: {
		//	for the time of the individual changes
		hour: '2-digit',
		minute: '2-digit',
		timeZoneName: 'short',
	} as Intl.DateTimeFormatOptions,
	dateAndTimePickerFormatOptions: {
		//	for displaying the current date and time in full for the datepicker input
		hour: '2-digit',
		minute: '2-digit',
		month: '2-digit',
		day: '2-digit',
		year: 'numeric',
		hour12: false,
	} as Intl.DateTimeFormatOptions,
	 requiredFields: ['eventType', 'route', 'dateStart'] as Array<keyof RoadEventDto>,
	showFormSectionMenu: true,
	topbarButtonTexts: {
		cancel: 'cancel',
		create: 'create',
		createAndClose: 'create & close',
		update: 'update',
		updateAndClose: 'update & close',
    complete: 'end event',
    createAndComplete: 'end event',
		reopen: 'reopen',
	},
	projectContacts: {
		maxContacts: 5,
		showContactType: false,
	},
	notifications: {
	  emailEnabledDefault: true,
		addAuthorAsRecipient: true,
		districtToEmailGroup: {
			[Districts.METRO_WEST]: 'Metro PACS',
			[Districts.METRO_EAST]: 'Metro PACS',
			[Districts.D1]: 'D1 PAC',
			[Districts.D2]: 'D2 PAC',
			[Districts.D3]: 'D3 PAC',
			[Districts.D4]: 'D4 PAC',
			[Districts.D6]: 'D6 PAC',
			[Districts.D7]: 'D7 PAC',
			[Districts.D8]: 'D8 PAC',
		},
  },
  priority: {
		determinePriorityByLaneImpacts: false,
		determinePriorityByUnplannedEvents: true,
	},
	notes: {
		showProjectWebsiteUrl: true,
		projectWebsiteError:
			'This URL is invalid. Please re-enter in the following format: www.example.com',
		showInternalNotes: true,
	},
	recurringScheduling: {
		showSetDatesToggle: false,
		showAllDayToggle: false,
	},
	timeline: {
		showSignUpdates: false,
		showInternalNotes: false,
	},
};

export const RelativeDateTimeEntries = {
  [RelativeDateTime.IMMEDIATELY]: 'Immediately',
  [RelativeDateTime.TOMORROW]: 'Tomorrow',
  [RelativeDateTime.WEEK]: 'Next Week',
  [RelativeDateTime.MONTH]: 'Next Month',
};

export const RelativeDurationEntries = {
	[RelativeDuration.INDEFINITELY]: 'Indefinitely',
	[RelativeDuration.HOUR]: 'One Hour',
	[RelativeDuration.TODAY]: 'Today',
	[RelativeDuration.DAY]: 'One Day',
	[RelativeDuration.WEEK]: 'One Week',
};

export enum EventAttributeValue {
  'Work Zone' = 'Work Zone',
  'CMV' = 'CMV',
  'Hazmat' = 'Hazmat',
  'Blocking' = 'Blocking',
  'Comment Card' = 'Comment Card',
  'Narcan' = 'Narcan',
}

export const EventAttributes: EventAttribute[] = [
  {
    value: EventAttributeValue['Work Zone'],
    label: EventAttributeValue['Work Zone'],
  },
  {
    value: EventAttributeValue.CMV,
    label: EventAttributeValue.CMV,
  },
  {
    value: EventAttributeValue.Hazmat,
    label: EventAttributeValue.Hazmat,
  },
  {
    value: EventAttributeValue.Blocking,
    label: EventAttributeValue.Blocking,
  },
  {
    value: EventAttributeValue['Comment Card'],
    label: EventAttributeValue['Comment Card'],
  },
  {
    value: EventAttributeValue.Narcan,
    label: EventAttributeValue.Narcan,
  },
];

export const REMSignPollingDelayMs = 10 * MILLISECONDS_PER_SECOND;

//  if any of these props change, the location details need to be refreshed
export const DRAFT_PROP_PATHS_THAT_INVALIDATE_LOCATION_DETAILS = [
  'route',
  'startMileMarker',
  'endMileMarker',
  'district',
];

export const DRAFT_PROP_PATHS_THAT_INVALIDATE_SIGNS = [
  'route',
  'startMileMarker',
  'endMileMarker',
  'positiveLaneBlockage.allLanesAffected',
  'negativeLaneBlockage.allLanesAffected',
];
//  if any of these properties change, the DMS previews need to be reloaded
export const DRAFT_PROP_PATHS_THAT_INVALIDATE_DMS_PREVIEW = [
  'dateEnd',
  'dateStart',
  'eventType',
  'phrases',
  'route',
  'startMileMarker',
  'endMileMarker',
  'positiveLaneBlockage',
  'negativeLaneBlockage',
  'positiveLaneBlockageType',
  'positiveLaneBlockage.lanesAffected',
  'positiveLaneBlockage.enteranceRampAffected',
  'positiveLaneBlockage.exitRampAffected',
  'positiveLaneBlockage.insideShoulderAffected',
  'positiveLaneBlockage.outsideShoulderAffected',
  'negativeLaneBlockageType',
  'negativeLaneBlockage.lanesAffected',
  'negativeLaneBlockage.enteranceRampAffected',
  'negativeLaneBlockage.exitRampAffected',
  'negativeLaneBlockage.insideShoulderAffected',
  'negativeLaneBlockage.outsideShoulderAffected',
];

// if any of these props change, the event description preview needs to be refreshed
export const DRAFT_PROP_PATHS_THAT_INVALIDATE_DESCRIPTION = [
  'eventType',
  'phrases',
  'route',
  'positiveLaneBlockage',
  'negativeLaneBlockage',
  'positiveLaneBlockageType',
  'positiveLaneBlockage.lanesAffected',
  'positiveLaneBlockage.enteranceRampAffected',
  'positiveLaneBlockage.exitRampAffected',
  'positiveLaneBlockage.insideShoulderAffected',
  'positiveLaneBlockage.outsideShoulderAffected',
  'negativeLaneBlockageType',
  'negativeLaneBlockage.lanesAffected',
  'negativeLaneBlockage.enteranceRampAffected',
  'negativeLaneBlockage.exitRampAffected',
  'negativeLaneBlockage.insideShoulderAffected',
  'negativeLaneBlockage.outsideShoulderAffected',
  'locationDetails',
  'locationDetails.city',
  'locationDetails.proximity',
  'locationDetails.crossStreetStart',
  'locationDetails.crossStreetEnd',
  'geometry',
  'additionalPublicText',
  'dateStart',
  'dateEnd',
  'quantities',
  'eventLocation',
  'positiveDirection',
  'negativeDirection',
  'rampPk',
  'detours',
  'scheduleWindows',
];

// if any of these props change, priority should be recalculated
export const DRAFT_PROP_PATHS_THAT_INVALIDATE_PRIORITY = [
  'eventType',
  'positiveLaneBlockage',
  'negativeLaneBlockage',
  'positiveLaneBlockageType',
  'positiveLaneBlockage.lanesAffected',
  'positiveLaneBlockage.enteranceRampAffected',
  'positiveLaneBlockage.exitRampAffected',
  'positiveLaneBlockage.insideShoulderAffected',
  'positiveLaneBlockage.outsideShoulderAffected',
  'negativeLaneBlockageType',
  'negativeLaneBlockage.lanesAffected',
  'negativeLaneBlockage.enteranceRampAffected',
  'negativeLaneBlockage.exitRampAffected',
  'negativeLaneBlockage.insideShoulderAffected',
  'negativeLaneBlockage.outsideShoulderAffected',
];

// if any of these props change, the event icon should be re-fetched
export const DRAFT_PROP_PATHS_THAT_INVALIDATE_ICON = [
  'eventType',
  'phrases',
  'eventLocation',
  'priorityLevel',
  'dateStart',
  'positiveLaneBlockage',
  'negativeLaneBlockage',
  'positiveLaneBlockageType',
  'positiveLaneBlockage.lanesAffected',
  'positiveLaneBlockage.enteranceRampAffected',
  'positiveLaneBlockage.exitRampAffected',
  'positiveLaneBlockage.insideShoulderAffected',
  'positiveLaneBlockage.outsideShoulderAffected',
  'negativeLaneBlockageType',
  'negativeLaneBlockage.lanesAffected',
  'negativeLaneBlockage.enteranceRampAffected',
  'negativeLaneBlockage.exitRampAffected',
  'negativeLaneBlockage.insideShoulderAffected',
  'negativeLaneBlockage.outsideShoulderAffected',
];

export const extraneousRouteOptions: ComboBoxOption[] = [
  {
    value: 'Other',
    label: 'Other',
  },
];

export const DEFAULT_PRIORITY_VALUE_FALLBACK = 5;

export const CARS_EVENT_INTEGRATION_ENABLED = true;

export const iconSizeByEventPriority = (priority: number): string => {
  if (priority === 1) return 'large';
  if (priority === 2 || priority === 3) return 'medium';

  return 'small';
};

export const iconSizes: Record<string, number> = {
  small: 20,
  medium: 28,
  large: 44,
};

// if an event occurs more than this configured time in the future, display its future icon on maps
export const FUTURE_EVENT_ICON_THRESHOLD = 2 * MILLISECONDS_PER_HOUR;

// these are the columns which will be rendered in the REM table view
export const activeREMTableColumns: typeof REM_EVENT_COLUMNS[number][] = [
  'type',
  'id',
  'route',
  'mileMarker',
  'direction',
  'district',
  'dateCreated',
  'schedule',
  'lastUser',
  'lastUpdated',
	'detours',
];

export interface ClusterConfig {
  minZoom: number;
  maxZoom: number;
  maxClusterDistancePx?: number;
  minClusterMembers?: number;
  excludeEventsBy?: {
    geometryType?: [GeoJsonGeometryTypes, ...GeoJsonGeometryTypes[]];
    priority?: [number, ...number[]];
    // namespace?: [RegExp | string, ...(RegExp | string)[]];
  };
  iconSrc: (resultCount: number) => string;
  iconSizePx: {
    width: number;
    height: number;
  };
  iconZIndex: number;
}

const ZINDEX_HIGHER_FEATURE = 1000;

export const clusterConfigEvents: ClusterConfig[] = [
  {
    excludeEventsBy: {
      // geometryType: ['Polygon', 'MultiPolygon'],
      priority: [1],
      // namespace: ['WazeAlerts', 'WazeJams', 'INSEG'],
    },
    maxClusterDistancePx: 28,
    minClusterMembers: 2,
    minZoom: 0,
    maxZoom: 8,
    iconSrc: (resultCount: number): string => {
      const prefix = `/img/cluster/icon-cluster-camera-`;
      const postfix = `-solid.svg`;
      if (resultCount >= 99) {
        return `${prefix}99plus${postfix}`;
      }
      if (resultCount >= 50) {
        return `${prefix}50plus${postfix}`;
      }
      if (resultCount >= 20) {
        return `${prefix}20plus${postfix}`;
      }
      if (resultCount > 10) {
        return `${prefix}10plus${postfix}`;
      }
      return `${prefix}${resultCount}${postfix}`;
    },
    iconSizePx: {
      width: 32,
      height: 32,
    },
    iconZIndex: ZINDEX_HIGHER_FEATURE,
  },
];

export const STATE_COMBOBOX_OPTIONS: ComboBoxOption[] = [
  { value: 'N/A', label: 'N/A' },
  { value: 'IN', label: 'IN' },
  { value: 'AL', label: 'AL' },
  { value: 'AK', label: 'AK' },
  { value: 'AZ', label: 'AZ' },
  { value: 'AR', label: 'AR' },
  { value: 'CA', label: 'CA' },
  { value: 'CO', label: 'CO' },
  { value: 'CT', label: 'CT' },
  { value: 'DE', label: 'DE' },
  { value: 'FL', label: 'FL' },
  { value: 'GA', label: 'GA' },
  { value: 'HI', label: 'HI' },
  { value: 'ID', label: 'ID' },
  { value: 'IL', label: 'IL' },
  { value: 'IA', label: 'IA' },
  { value: 'KS', label: 'KS' },
  { value: 'KY', label: 'KY' },
  { value: 'LA', label: 'LA' },
  { value: 'ME', label: 'ME' },
  { value: 'MD', label: 'MD' },
  { value: 'MA', label: 'MA' },
  { value: 'MI', label: 'MI' },
  { value: 'MN', label: 'MN' },
  { value: 'MS', label: 'MS' },
  { value: 'MO', label: 'MO' },
  { value: 'MT', label: 'MT' },
  { value: 'NE', label: 'NE' },
  { value: 'NV', label: 'NV' },
  { value: 'NH', label: 'NH' },
  { value: 'NJ', label: 'NJ' },
  { value: 'NM', label: 'NM' },
  { value: 'NY', label: 'NY' },
  { value: 'NC', label: 'NC' },
  { value: 'ND', label: 'ND' },
  { value: 'OH', label: 'OH' },
  { value: 'OK', label: 'OK' },
  { value: 'OR', label: 'OR' },
  { value: 'PA', label: 'PA' },
  { value: 'RI', label: 'RI' },
  { value: 'SC', label: 'SC' },
  { value: 'SD', label: 'SD' },
  { value: 'TN', label: 'TN' },
  { value: 'TX', label: 'TX' },
  { value: 'UT', label: 'UT' },
  { value: 'VT', label: 'VT' },
  { value: 'VA', label: 'VA' },
  { value: 'WA', label: 'WA' },
  { value: 'WV', label: 'WV' },
  { value: 'WI', label: 'WI' },
  { value: 'WY', label: 'WY' },
];

export const getEmptyDraftEvent = (): DraftEvent => ({
  eventType: undefined,
  eventSource: ConfigREMEventForm.source.defaultSource,
  route: undefined,
  startMileMarker: undefined,
  eventLocation: LocationTypes.MAJOR_ROAD,
  dateStart: Date.now(),
  priorityLevel: 5,
  emailEnabled: ConfigREMEventForm.notifications.emailEnabledDefault,
});

export const getEventTitle = (event: RoadEventDto): string =>
	`[#${event.id}] ${event.eventType} on ${event.route}${
		isValidNumber(event.startMileMarker) ? ` @ mile ${event.startMileMarker.toFixed(1)}` : ''
	}${isValidNumber(event.endMileMarker) ? ` - ${event.endMileMarker.toFixed(1)}` : ''}
		`.trim();

export const IMPORTED_EVENT_SOURCES = ['MSPCAD'];
